import { useLayoutEffect } from 'react';

/** Set on the accordion root; caps the open panel so inner content can scroll. */
export const SECTIONED_ACCORDION_OPEN_MAX_CSS_VAR = '--accordion-sectioned-open-max-h';

const ITEM_SELECTOR = ':scope > [data-slot=accordion-item]';
const CONTENT_SLOT = '[data-slot=accordion-content]';

function queryDirectItems(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll(ITEM_SELECTOR)] as HTMLElement[];
}

function isOpenItem(item: HTMLElement): boolean {
  return item.getAttribute('data-state') === 'open';
}

function getHeaderHeight(item: HTMLElement, content: HTMLElement | null): number {
  if (!content) return item.offsetHeight;
  return content.getBoundingClientRect().top - item.getBoundingClientRect().top;
}

/**
 * Vertical space for each open panel’s body: root height minus closed rows and open headers,
 * split evenly between open panels. Returns null when no item is open.
 */
export function measureSectionedAccordionOpenContentMaxPx(root: HTMLElement): number | null {
  const items = queryDirectItems(root);
  const openCount = items.filter(isOpenItem).length;

  if (openCount === 0) return null;

  const reserved = items.reduce((sum, item) => {
    if (!isOpenItem(item)) return sum + item.offsetHeight;

    return sum + getHeaderHeight(item, item.querySelector(CONTENT_SLOT) as HTMLElement | null);
  }, 0);

  return Math.max(0, Math.floor((root.clientHeight - reserved) / openCount));
}

function syncOpenMaxVar(root: HTMLElement): void {
  const maxPx = measureSectionedAccordionOpenContentMaxPx(root);
  if (maxPx === null) root.style.removeProperty(SECTIONED_ACCORDION_OPEN_MAX_CSS_VAR);
  else root.style.setProperty(SECTIONED_ACCORDION_OPEN_MAX_CSS_VAR, `${maxPx}px`);
}

/** Writes `SECTIONED_ACCORDION_OPEN_MAX_CSS_VAR` on `rootRef.current` when layout or open item changes. */
export function useSectionedAccordionLayout(rootRef: React.RefObject<HTMLElement | null>, enabled: boolean): void {
  useLayoutEffect(() => {
    if (!enabled || !rootRef.current) return;

    const root = rootRef.current;
    const observedItems = new Set<Element>();

    const update = () => {
      syncOpenMaxVar(root);
    };

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(root);

    const observeNewItems = () => {
      for (const item of root.querySelectorAll(ITEM_SELECTOR)) {
        if (observedItems.has(item)) continue;
        resizeObserver.observe(item);
        observedItems.add(item);
      }
    };

    observeNewItems();
    update();

    const mutationObserver = new MutationObserver(() => {
      observeNewItems();
      update();
    });
    mutationObserver.observe(root, { childList: true });

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      observedItems.clear();
      root.style.removeProperty(SECTIONED_ACCORDION_OPEN_MAX_CSS_VAR);
    };
  }, [enabled, rootRef]);
}
